import {noop} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatDraftStatus, createChatWithDraftMessage} from 'llm/chat-library/ChatActions';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {getNoteLinkedFile} from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {NoteLinkedFileTabState} from 'notebook/notes/attachments/NoteLinkedFileTab';
import type {OmniboxActionItem} from 'omnibox/OmniboxAction';
import type {ContentResult, OmniboxHelpDocsResult, OmniboxResponse, RecentSearch} from 'omnibox/omniboxTypes';

import {
  CONTENT_MATCHES_GROUP,
  MAX_ATTACHMENT_ACTIONS,
  MAX_RESULT_ACTIONS,
  RECENT_SEARCHES_LIMIT,
  SEARCH_RESULTS_GROUP,
} from './omniboxConstants';

let attachmentPreviewAbortController: AbortController | null = null;

export function abortAttachmentPreview(): void {
  attachmentPreviewAbortController?.abort();
}

async function openAskWithQuery(rewrittenQuery: string): Promise<void> {
  const selectedAgentType = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.ASK;
  const chatType = getChatTypeForNewAgentChat(selectedAgentType);
  const {chatSessionId} = await createChatWithDraftMessage(chatType, {
    draft: {
      status: ChatDraftStatus.DRAFT,
      message: {text: rewrittenQuery, selectedFiles: []},
      settings: {
        agentType: selectedAgentType,
        publicDataEnabled: false,
      },
    },
  });
  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);
}

function openNoteLinkedFileTab(
  entryApiId: string,
  fileId: string,
  fileName: string,
  previewFile: Parameters<typeof NoteLinkedFileTabState.open>[3] | null,
  externalFile: Parameters<typeof NoteLinkedFileTabState.open>[4],
  isPreviewTransformed: boolean
): void {
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    const existingTab = fileState.findTab(
      NoteLinkedFileTabState,
      (tab: NoteLinkedFileTabState) => tab.noteLinkedFileId() === fileId
    );
    if (existingTab != null) {
      existingTab.set({active: true});
      return;
    }
    fileState.buffers().addTab(
      new NoteLinkedFileTabState({
        owningEntityApiId: entryApiId,
        noteLinkedFileId: fileId,
        fileName,
        previewFile,
        externalFile,
        isPreviewTransformed,
      })
    );
  }
}

export async function openAttachmentPreview(
  entryApiId: string,
  noteLinkedFileId: string | null
): Promise<void> {
  if (!noteLinkedFileId) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  let file;
  try {
    file = await getNoteLinkedFile({uuid: noteLinkedFileId, entryId: entryApiId});
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  if (!file.externalFile) {
    FileBrowserActions.openItem(entryApiId);
    return;
  }

  // If the entry is already open, add the tab directly
  const fileState = FileStates.get().get(entryApiId);
  if (fileState?.buffers()) {
    openNoteLinkedFileTab(
      entryApiId,
      file.id,
      file.name,
      file.previewFile,
      file.externalFile,
      file.isPreviewTransformed ?? false
    );
    return;
  }

  // Entry not open — navigate to it first, then add the tab once it loads
  FileBrowserActions.openItem(entryApiId);
  attachmentPreviewAbortController?.abort();
  attachmentPreviewAbortController = new AbortController();
  const signal = attachmentPreviewAbortController.signal;

  const tryOpen = (): boolean => {
    const state = FileStates.get().get(entryApiId);
    if (state?.buffers()) {
      openNoteLinkedFileTab(
        entryApiId,
        file.id,
        file.name,
        file.previewFile,
        file.externalFile!,
        file.isPreviewTransformed ?? false
      );
      return true;
    }
    return false;
  };

  if (tryOpen()) {
    return;
  }

  let cleaned = false;
  const cleanup = (): void => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    FileStates.get().off('add change', listener);
    clearTimeout(timeout);
    signal.removeEventListener('abort', cleanup);
  };

  const listener = (): void => {
    if (signal.aborted) {
      cleanup();
      return;
    }
    if (tryOpen()) {
      cleanup();
    }
  };

  const timeout = setTimeout(cleanup, 5000);
  FileStates.get().on('add change', listener);
  signal.addEventListener('abort', cleanup);
}

export function buildDefaultActions(
  recentSearches: Array<RecentSearch>,
  onRecentSearchTrigger: (query: string) => void
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  for (const item of recentSearches.slice(0, RECENT_SEARCHES_LIMIT)) {
    actions.push({
      id: `recent-search-${item.queryString}`,
      title: item.queryString,
      group: 'Recent Searches',
      resultType: 'recent_search',
      apiId: item.openedApiId,
      objectType: item.openedObjectType,
      closeOnTrigger: false,
      onTrigger: () => {
        onRecentSearchTrigger(item.queryString);
      },
    });
  }

  return actions;
}

export function buildActions(
  // Nullable so attachment matches from the parallel /omnibox/attachments fetch can render before
  // the main search response arrives; a null main response contributes no search/content rows.
  results: OmniboxResponse | null,
  copiedAnswer: boolean,
  onCopyAnswer: () => void,
  helpDocsResults: Map<string, OmniboxHelpDocsResult | 'loading'>,
  attachmentResults: Array<ContentResult> | null = null
): Array<OmniboxActionItem> {
  const actions: Array<OmniboxActionItem> = [];

  if (results?.directAnswer) {
    const answerText = results.directAnswer;
    actions.push({
      id: 'direct-answer',
      title: answerText,
      description: copiedAnswer ? 'Copied to clipboard!' : undefined,
      group: 'Answer',
      resultType: 'direct_answer',
      onTrigger: () => {
        navigator.clipboard.writeText(answerText).catch((e) => {
          // eslint-disable-next-line no-console
          console.error('Failed to copy answer to clipboard', e);
        });
        onCopyAnswer();
      },
      closeOnTrigger: false,
    });
  }

  // Search results and entry-content matches share the main list; attachments render in their own
  // "Content matches" section with a separate budget. Sliced to their limits below.
  const searchActions: Array<OmniboxActionItem> = [];
  const entryContentActions: Array<OmniboxActionItem> = [];
  const attachmentActions: Array<OmniboxActionItem> = [];

  const searchResultApiIds = new Set<string>();
  for (const result of results?.searchResults ?? []) {
    searchResultApiIds.add(result.apiId);
    searchActions.push({
      id: `search-${result.apiId}`,
      title: result.name,
      modifiedAt: result.modifiedAt,
      relevanceReason: result.relevanceReason,
      // Kept for CLICKED_RESULT analytics; the section header itself is suppressed in Omnibox.
      group: SEARCH_RESULTS_GROUP,
      objectType: result.objectType,
      resultType: 'search_result',
      apiId: result.apiId,
      onTrigger: () => {
        FileBrowserActions.openItem(result.apiId);
      },
    });
  }

  // Keep one row per entry: extra chunks share a name and click target, so without dedup they'd
  // render as duplicate rows. Results arrive in relevance order, so the first kept is the strongest.
  // The search-only path already collapses server-side; the LLM path doesn't, so this covers both.
  // Attachments come from the parallel /omnibox/attachments fetch (attachmentResults) below, so any
  // attachment rows in contentResults are ignored here to avoid rendering them twice.
  const seenEntryApiIds = new Set(searchResultApiIds);
  for (const [index, contentResult] of (results?.contentResults ?? []).entries()) {
    if (contentResult.attachmentApiId != null || seenEntryApiIds.has(contentResult.entryApiId)) {
      continue;
    }
    seenEntryApiIds.add(contentResult.entryApiId);
    entryContentActions.push({
      id: `content-${contentResult.entryApiId}-${index}`,
      // Entry content matches render like a normal Entry result.
      title: contentResult.entryName ?? 'Unknown',
      objectType: 'Entry',
      modifiedAt: contentResult.modifiedAt,
      relevanceReason: contentResult.relevanceReason,
      group: SEARCH_RESULTS_GROUP,
      resultType: 'content',
      apiId: contentResult.entryApiId,
      entryApiId: contentResult.entryApiId,
      onTrigger: () => {
        openAttachmentPreview(contentResult.entryApiId, contentResult.noteLinkedFileId);
      },
    });
  }

  // Attachment matches populate their own "Content matches" section from the parallel fetch, so they
  // render whenever that request resolves, independent of the main search. Dedup by file (extra chunks
  // of one attachment share a name and click target).
  const seenAttachmentApiIds = new Set<string>();
  for (const [index, contentResult] of (attachmentResults ?? []).entries()) {
    if (contentResult.attachmentApiId == null || seenAttachmentApiIds.has(contentResult.attachmentApiId)) {
      continue;
    }
    seenAttachmentApiIds.add(contentResult.attachmentApiId);
    attachmentActions.push({
      id: `content-${contentResult.attachmentApiId}-${index}`,
      // Attachments keep their file name, falling back to the parent entry name.
      title: contentResult.attachmentName || `Entry ${contentResult.entryName ?? 'Unknown'}`,
      modifiedAt: contentResult.modifiedAt,
      relevanceReason: contentResult.relevanceReason,
      group: CONTENT_MATCHES_GROUP,
      resultType: 'content',
      entryApiId: contentResult.entryApiId,
      onTrigger: () => {
        openAttachmentPreview(contentResult.entryApiId, contentResult.noteLinkedFileId);
      },
    });
  }

  const primaryActions = [...searchActions, ...entryContentActions];
  actions.push(
    ...primaryActions.slice(0, MAX_RESULT_ACTIONS),
    ...attachmentActions.slice(0, MAX_ATTACHMENT_ACTIONS)
  );

  for (const suggestion of results?.askSuggestions ?? []) {
    if (suggestion.type === 'help_docs') {
      const helpDocsResult = helpDocsResults.get(suggestion.rewrittenQuery);
      const isLoading = helpDocsResult === 'loading';
      const hasResult = helpDocsResult != null && helpDocsResult !== 'loading';
      const hasError = hasResult && helpDocsResult.error != null;
      const hasArticles = hasResult && !hasError && helpDocsResult.articles.length > 0;

      if (isLoading) {
        actions.push({
          id: `help-docs-loading-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: 'Searching help docs...',
          description: suggestion.reason,
          group: 'Help Docs',
          resultType: 'help_docs_loading',
          onTrigger: noop,
          closeOnTrigger: false,
        });
      } else if (hasArticles) {
        for (const article of helpDocsResult.articles) {
          actions.push({
            id: `help-doc-${article.url.slice(-30)}`,
            title: article.title,
            description: article.paraphrase,
            group: 'Help Docs',
            resultType: 'help_docs_article',
            onTrigger: () => {
              window.open(article.url, '_blank', 'noopener,noreferrer');
            },
          });
        }
      } else {
        // No results, error, or still pending — fall back to Ask Benchling
        actions.push({
          id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
          title: hasError ? 'Help docs search failed' : `Ask: ${suggestion.rewrittenQuery}`,
          description: hasError ? helpDocsResult.error! : suggestion.reason,
          group: 'Ask Benchling',
          resultType: 'ask_suggestion',
          onTrigger: () => {
            openAskWithQuery(suggestion.rewrittenQuery);
          },
        });
      }
    } else {
      actions.push({
        id: `ask-${suggestion.type}-${suggestion.rewrittenQuery.slice(0, 30)}`,
        title: `Ask: ${suggestion.rewrittenQuery}`,
        description: suggestion.reason,
        group: 'Ask Benchling',
        resultType: 'ask_suggestion',
        onTrigger: () => {
          openAskWithQuery(suggestion.rewrittenQuery);
        },
      });
    }
  }

  return actions;
}
